<template>
  <div class="row justify-center">
    <div class="col-12 text-center q-py-none q-px-lg">
      <q-tabs
        v-model="selectedTab"
        inverted
        class="q-pb-lg text-primary"
        v-show="localAgencies.length && !auditRelated && !qrCode"
      >
        <q-tab
          name="mon-secteur"
          icon="person_pin_circle"
          :label="$t('common.myScope')"
        />
        <q-tab
          name="autre"
          icon="add_circle_outline"
          :label="$t('common.other')"
          v-if="!auditRelated"
        />
      </q-tabs>
      <!-- Top Zone -->
      <q-field
        v-if="!auditRelated"
        borderless
        v-show="agencies.length > 1"
        :error="v$.selectedAgency.$error"
      >
        <template v-slot:before>
          <q-icon name="store_mall_directory" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedAgency"
              :label="$t('common.agency')"
              :options="agencySelectOptions"
            />
          </div>
        </template>
      </q-field>

      <!-- Zone -->
      <q-field
        v-if="!auditRelated"
        borderless
        v-show="residences.length > 1 && selectedAgencyId"
        :error="v$.selectedResidence.$error"
      >
        <template v-slot:before>
          <q-icon name="location_city" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedResidence"
              :label="$t('issueDetails.residence')"
              :options="residenceSelectOptions"
            />
          </div>
        </template>
      </q-field>

      <!-- Place -->
      <q-field
        v-if="!auditRelated && selectedResidenceId"
        borderless
        :error="v$.selectedPlace.$error"
      >
        <template v-slot:before>
          <q-icon name="place" color="primary" />
        </template>
        <template v-slot:control>
          <div
            v-if="!isLoadingPlaces && placeSelectOptions.length === 0"
            class="self-center full-width no-outline"
            tabindex="0"
          >
            <q-banner dense inline-actions class="text-black bg-red-2">
              {{ $t('createIssue.noSpotOnselectedPlace') }}
            </q-banner>
          </div>
          <div v-else class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedPlace"
              :label="$t('issueDetails.place')"
              :options="placeSelectOptions"
            />
          </div>
        </template>
      </q-field>

      <!-- Spot -->
      <q-field
        borderless
        v-if="spots.length > 1 && selectedPlace.value"
        :error="v$.selectedSpot.$error"
        :disable="isVacant"
      >
        <template v-slot:before>
          <q-icon name="my_location" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedSpot"
              :label="$t('issueDetails.spot')"
              :options="spotSelectOptions"
            />
          </div>
        </template>
      </q-field>
      <!-- reasons -->
      <q-field
        borderless
        v-if="spots.length && reasons.length && selectedSpotId"
        :error="v$.selectedReason.$error"
      >
        <template v-slot:before>
          <q-icon name="apps" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedReason"
              :label="$t('issueDetails.reason')"
              :options="reasons"
            />
          </div>
        </template>
      </q-field>

      <!-- Pictures -->
      <q-field borderless v-if="images.length > 0">
        <template v-slot:before>
          <q-icon name="camera_alt" color="primary" />
        </template>
        <template v-slot:control>
          <div
            class="row q-mb-lg full-width q-pr-none"
            v-for="(img, index) in images"
            :key="index"
            style="border: 1px dotted #d3d3d3"
          >
            <div
              class="col text-center"
              ref="imgContainer"
              style="position: relative"
            >
              <img :src="img" style="max-width: 100%" />
              <q-btn
                color="negative"
                round
                icon="clear"
                size="sm"
                @click="removeImg(index)"
                class="removeImg"
              />
            </div>
          </div>
        </template>
      </q-field>
      <!-- RGPD Mentions -->
      <q-field borderless class="rgpdField q-py-md">
        <q-icon name="info" color="primary" class="icon-rgpd" />
        <div @click="rgpdDialog = true" class="rgpdButton">
          {{ $t('createIssue.rgpd') }}
        </div>
      </q-field>
      <!-- Details -->
      <q-field
        borderless
        :error="v$.message.$error"
        :helper="$t('issueDetails.specifyIssue')"
        v-model="message"
        :counter="v$.message.maxLength.$invalid"
        maxlength="1024"
      >
        <template v-slot:before>
          <q-icon name="list" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <q-input
              :placeholder="$t('common.details')"
              borderless
              type="textarea"
              name="detail"
              v-model="message"
              class="bordered"
              rows="5"
              tabindex="3"
              @blur="v$.message.$touch"
              ref="message"
            />
          </div>
        </template>
      </q-field>
      <q-btn
        v-if="($q.platform.is.capacitor || qrCode) && images.length < 3"
        color="primary"
        round
        icon="camera_alt"
        size="sm"
        @click="openActionSheet()"
        style="top: -155px"
        class="buttonHover"
      />

      <!-- Priority -->
      <q-field borderless>
        <template v-slot:before>
          <q-icon name="report_problem" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <q-slider
              v-model="priority"
              :min="-1"
              :max="1"
              :step="1"
              :label="true"
              :label-always="true"
              :snap="true"
              :markers="true"
              :label-value="$helpers.priorityQuasarIdToFrench(priority)"
            />
          </div>
        </template>
      </q-field>

      <!-- Set private -->
      <q-field borderless v-if="company.showIsPrivate">
        <template v-slot:before>
          <q-icon name="pan_tool" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <q-checkbox
              v-model="isPrivate"
              :label="$t('createIssue.private')"
              color="primary"
            />
          </div>
        </template>
      </q-field>
      <q-field borderless v-if="forwardIssueHasAttribute('url')">
        <template v-slot:before>
          <q-icon name="arrow_forward" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <q-checkbox
              v-model="toForward"
              :label="
                forwardIssueHasAttribute('message')
                  ? company.forwardIssues.message
                  : $t('issueDetails.forwardIssuesMessage')
              "
              color="primary"
            />
          </div>
        </template>
      </q-field>
      <!-- tenant's request -->
      <q-field borderless v-if="isAllowedOnLocativesCategory">
        <template v-slot:before>
          <q-icon>
            <img src="~assets/account-arrow-right.svg" color="primary" />
          </q-icon>
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <q-checkbox
              v-model="isRequestTenant"
              :label="$t('createIssue.requestTenant')"
              color="primary"
            />
          </div>
        </template>
      </q-field>
    </div>
    <div class="row">
      <div class="col text-center">
        <q-btn
          small
          color="primary"
          @click="send"
          :disabled="disableSubmitButton"
          class="q-mt-xl"
          style="height: 60"
        >
          <q-icon left size="sm" name="save_alt" />
          <div>{{ $t('common.save') }}</div>
        </q-btn>
      </div>
    </div>
    <q-dialog v-model="rgpdDialog">
      <q-card>
        <q-card-section class="row items-center q-pb-none rgpd">
          <div>{{ $t('createIssue.rgpdInfo') }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup absolute-right />
        </q-card-section>

        <q-card-section class="rgpd">
          « {{ $t('createIssue.rgpdInfoDetails.personalData') }},
          <b>{{ $t('createIssue.rgpdInfoDetails.accessComment') }}</b>
          {{ $t('createIssue.rgpdInfoDetails.dpo') }} »
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxLength } from '@vuelidate/validators'
import heic2any from 'heic2any'
import useVuelidate from '@vuelidate/core'
import eventBus from 'src/eventBus'
const uuidv1 = require('uuid/v1')
import SwFilterableSelect from 'src/components/inputs/sw-filterable-select.vue'
import moment from 'moment'
import { alphabeticalSort } from 'src/utils/sort'
import { getCheckpointCategoryId } from 'src/utils/checkpoints'
import { findCategoryById, findTopCategoryId } from 'src/utils/categories'

export default {
  inject: ['$DB', '$helpers'],
  props: ['category', 'audit'],
  components: { SwFilterableSelect },
  setup() {
    return { v$: useVuelidate() }
  },

  data() {
    return {
      userPatrimoine: [],
      rgpdDialog: false,
      agencies: [],
      residences: [],
      places: [],
      spots: [],
      reasons: [],
      selectedTab: 'mon-secteur',
      selectedAgency: { label: '', value: '' },
      selectedReason: { label: '', value: '' },
      selectedResidence: { label: '', value: '' },
      selectedPlace: { label: '', value: '' },
      selectedSpot: { label: '', value: '' },
      message: '',
      images: [],
      priority: 0,
      disableSubmitButton: false,
      isRequestTenant: false,
      isPrivate: false,
      auditRelated: false,
      toForward: false,
      actionSheetConfig: {
        actions: [
          {
            label: this.$t('createIssue.openGallery'),
            icon: 'add_photo_alternate',
            color: 'primary',
            handler: () => {
              this.openGallery()
            }
          },
          {
            label: this.$t('createIssue.takePicture'),
            icon: 'add_a_photo',
            color: 'primary',
            handler: () => {
              this.shootPhoto()
            }
          }
        ],
        dismissLabel: this.$t('createIssue.dismissLabel')
      },
      issueCategory: null,
      fieldsToValidate: [
        'selectedAgency',
        'selectedResidence',
        'selectedPlace',
        'selectedSpot',
        'selectedReason'
      ],
      spotSelectOptions: [],
      reasonSelectOptions: [],
      isLoadingPlaces: false,
      callbackLocation: null
    }
  },
  validations() {
    return {
      selectedAgency: {
        value: { required }
      },
      selectedResidence: {
        value: { required }
      },
      selectedPlace: {
        value: { required }
      },
      selectedSpot: {
        value: { required }
      },
      message: {
        required,
        maxLength: maxLength(1024)
      },
      selectedReason: {
        value: {
          requiredIf: requiredIf(() => this.reasons.length > 0)
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/attributes',
      categories: 'company/categories',
      localAgencies: 'reporter/agencies',
      localResidences: 'reporter/residences',
      localPlaces: 'reporter/places',
      qrCode: 'reporter/qrCode'
    }),
    // NOTE: RUN-2927 — les spots portent une catégorie de premier niveau ;
    // issueCategory est une sous-catégorie dans le cas nominal, mais peut être
    // une racine lorsqu'on retombe sur la catégorie du checklist
    issueTopCategoryId() {
      return findTopCategoryId(this.categories, this.issueCategory?.id)
    },
    isAllowedOnLocativesCategory() {
      return this.categories.some((category) => {
        return (
          category.allowTenantRequest &&
          category.childs.some(
            (subCategory) => subCategory.id === this.category?.id
          )
        )
      })
    },
    isVacant() {
      return Boolean(this.$route.query.spot)
    },
    selectedAgencyId() {
      return this.selectedAgency instanceof Object
        ? this.selectedAgency?.value
        : this.selectedAgency
    },
    selectedResidenceId() {
      return this.selectedResidence?.value
    },
    selectedPlaceId() {
      return this.selectedPlace?.value
    },
    selectedSpotId() {
      if (this.reasons.length === 1) {
        this.selectedReason = this.reasons[0]
      } else {
        this.selectedReason == { label: '', value: '' }
      }
      return this.selectedSpot instanceof Object
        ? this.selectedSpot?.value
        : this.selectedSpot
    },
    selectedReasonId() {
      return this.selectedReason instanceof Object
        ? this.selectedReason?.value
        : this.selectedReason
    },
    agencySelectOptions() {
      return this.sortList(this.$helpers.resourcesToFormOptions(this.agencies))
    },
    residenceSelectOptions() {
      return this.sortList(
        this.$helpers.resourcesToFormOptions(this.residences)
      )
    },
    placeSelectOptions() {
      return alphabeticalSort(
        this.$helpers.resourcesToFormOptions(this.places),
        (option) => option.label
      )
    }
  },
  methods: {
    async deviceReady() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { Camera } = await import('Capacitor/@capacitor/camera')
        this.camera = Camera
      }
    },
    openActionSheet() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        this.$q
          .bottomSheet({
            class: this.$q.platform.is.android ? 'q-pb-xl' : 'q-pb-sm',
            actions: [
              {
                label: this.$t('createIssue.openGallery'),
                icon: 'add_photo_alternate',
                color: 'primary',
                id: 'photoGalery'
              },
              {
                label: this.$t('createIssue.takePicture'),
                icon: 'add_a_photo',
                color: 'primary',
                id: 'takePhoto'
              }
            ]
          })
          .onOk((action) => {
            this.handleActionSheetEvent(action.id)
          })
          .onCancel(() => {
            // console.log('Dismissed')
          })
          .onDismiss(() => {
            // console.log('I am triggered on both OK and Cancel')
          })
      } else {
        this.handleActionSheetEvent('photoGalery')
      }
    },
    handleActionSheetEvent(actionId) {
      switch (actionId) {
        case 'photoGalery':
          this.openGallery()
          break
        case 'takePhoto':
          this.shootPhoto()
          break
      }
    },
    async shootPhoto() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { CameraResultType, CameraSource } = await import(
          'Capacitor/@capacitor/camera'
        )
        const image = await this.camera.getPhoto({
          quality: 60,
          allowEditing: false,
          width: 1280,
          height: 720,
          source: CameraSource.Camera,
          resultType: CameraResultType.Base64,
          promptLabelCancel: this.$t('common.cancel'),
          promptLabelPhoto: this.$t('createIssue.openGallery'),
          promptLabelPicture: this.$t('createIssue.takePicture')
        })
        this.shootSuccess(image.base64String)
      }
    },
    async openGallery() {
      if (this.$q.platform.is.capacitor || this.qrCode) {
        const { CameraResultType, CameraSource } = await import(
          'Capacitor/@capacitor/camera'
        )
        const image = await this.camera.getPhoto({
          quality: 60,
          allowEditing: false,
          width: 1280,
          height: 720,
          source: CameraSource.Photos,
          resultType: CameraResultType.Base64,
          promptLabelCancel: this.$t('common.cancel'),
          promptLabelPhoto: this.$t('createIssue.openGallery'),
          promptLabelPicture: this.$t('createIssue.takePicture')
        })
        this.shootSuccess(image.base64String)
      }
    },
    removeImg(index) {
      this.images.splice(index, 1)
    },
    shootSuccess(imageData) {
      if (
        imageData.toUpperCase().endsWith('.HEIC') ||
        imageData.toUpperCase().endsWith('.HEIF')
      ) {
        fetch(imageData)
          .then((res) => res.blob())
          .then((blob) =>
            heic2any({
              blob,
              toType: 'image/jpeg',
              quality: 1
            })
          )
          .then((conversionResult) => {
            var reader = new FileReader()
            reader.onload = function () {
              var base64img = reader.result
              this.images.push(base64img)
            }
            reader.readAsDataURL(conversionResult)
          })
      } else {
        this.images.push('data:image/jpeg;base64,' + imageData)
      }
    },
    getCheckpointCategory() {
      // NOTE: RUN-2927 — le checkpoint peut manquer (deep link obsolète) ou ne
      // pas porter de catégorie (audit local d'une version antérieure, ou
      // checklist non catégorisé) : on retombe alors sur celle du checklist
      const checkpoints = Array.isArray(this.audit.checkpoints)
        ? this.audit.checkpoints
        : []
      const currentCheckpoint = checkpoints.find(
        (element) => Number(element.id) === Number(this.$route.query.checkpoint)
      )
      const categoryId = getCheckpointCategoryId(currentCheckpoint)
      return findCategoryById(this.categories, categoryId) || this.category
    },
    forwardIssueHasAttribute(info) {
      return (
        this.company.forwardIssues &&
        info in this.company.forwardIssues &&
        this.company.forwardIssues[info]
      )
    },
    getErrorsMessages() {
      return {
        selectedAgency: {
          message: this.$t('createIssue.agencyEmpty')
        },
        selectedResidence: {
          message: this.$t('createIssue.residenceEmpty')
        },
        selectedPlace: {
          message: this.$t('createIssue.placeEmpty')
        },
        selectedSpot: {
          message: this.$t('createIssue.spotEmpty')
        },
        defaultMessage: this.$t('common.error.completeAllInformation')
      }
    },
    validateField(field) {
      if (!this.v$[field].$error) return true

      let fieldsMapping = {
        selectedAgency: 'agencies',
        selectedResidence: 'residences',
        selectedPlace: 'places',
        selectedSpot: 'spots',
        selectedReason: 'reasons'
      }
      let errorsMessage = this.getErrorsMessages()
      this.$q.notify({
        message:
          this.$data[fieldsMapping[field]].length === 0
            ? errorsMessage[field].message
            : errorsMessage.defaultMessage,
        color: 'warning',
        position: 'top'
      })
    },
    send() {
      if (this.spots.length === 1) {
        this.selectedSpot = {
          label: this.spots[0].name,
          value: this.spots[0].id
        }
      }

      const fieldsToValidate = this.auditRelated
        ? ['selectedSpot', 'selectedReason']
        : this.fieldsToValidate

      this.v$.$touch()
      const errorsInFields = fieldsToValidate.some((field) => {
        return !this.validateField(field)
      })
      if (errorsInFields) {
        return
      }
      if (this.v$.message.$error) {
        this.$q.notify({
          message: this.getErrorsMessages().defaultMessage,
          color: 'warning',
          position: 'top'
        })
        return
      }

      this.disableSubmitButton = true

      let tmpIssue = {
        _id: uuidv1(),
        type: 'issues',
        event: 'new',
        message: this.message,
        'is-private': this.isPrivate,
        'is-request-tenant': this.isRequestTenant,
        disturbance: this.$helpers.priorityQuasarIdToApiId(this.priority),
        place: {
          id: this.selectedPlaceId,
          name: this.selectedPlace.label
        },
        spot: {
          id: this.selectedSpotId
        },
        category: {
          id: this.issueCategory.id
        },
        report: {
          id: null
        }
      }

      if (this.images.length > 0) {
        tmpIssue.imgs = this.images
      }
      if (this.selectedReasonId) {
        tmpIssue.reason = {
          id: parseInt(this.selectedReasonId)
        }
      }
      if (this.forwardIssueHasAttribute('url') && this.toForward) {
        tmpIssue.toForward = this.toForward
      }
      this.$DB
        .tmpIssues()
        .find({
          selector: {
            message: tmpIssue.message,
            category: { $eq: tmpIssue.category },
            spot: { $eq: tmpIssue.spot },
            report: { $eq: tmpIssue.report }
          }
        })
        .then((res) => {
          if (res.docs.length == 0) {
            tmpIssue.scheduledAt = moment().format('YYYY/MM/DD HH:mm')
            if (this.auditRelated) {
              tmpIssue.checkpoint = { id: this.$route.query.checkpoint }
              this.$DB
                .tmpReports()
                .get(this.$route.query.report)
                .then((report) => {
                  report.issues.push(tmpIssue)
                  const storedCheckpoint = report.checkpoints.find(
                    (c) => Number(c.id) === Number(this.$route.query.checkpoint)
                  )
                  storedCheckpoint.asserted = false
                  storedCheckpoint.missing = null
                  this.$DB
                    .tmpReports()
                    .put(report)
                    .then(() => {
                      this.$router.go(-1)
                    })
                })
            } else {
              delete tmpIssue.report
              this.$DB
                .tmpIssues()
                .put(tmpIssue)
                .then(() => {
                  const routeOption = {
                    path: '/categories'
                  }
                  if (this.qrCode) {
                    routeOption.query = {
                      location: this.$route.query.location
                    }
                  }
                  this.$router.push(routeOption)
                  this.$store.dispatch('audits/createNotification', true)
                  eventBus.$emit('processQueue')
                })
            }
          }
        })
        .catch((err) => {
          console.log('err writing tmpIssues', err)
          this.$q.notify({
            message: this.$t('createIssue.sendIssueError'),
            color: 'negative',
            position: 'top'
          })
          this.disableSubmitButton = false
        })
    },
    onKeyboardDidShow() {
      this.$nextTick(() => {
        const el = this.$refs.message?.$el
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    sortList(tabOptions) {
      return tabOptions.sort((a, b) => {
        if (a.label === null) {
          return true
        } else {
          return String(a.label).localeCompare(String(b.label), 'fr', {
            ignorePunctuation: true,
            numeric: true,
            sensitivity: 'base'
          })
        }
      })
    },
    async selectAgency(agency) {
      this.selectedResidence = { label: '', value: '' }
      this.selectedPlace = { label: '', value: '' }
      this.selectedSpot = { label: '', value: '' }
      this.selectedReason = { label: '', value: '' }
      if (agency) {
        this.selectedAgency = { label: agency.name, value: agency.id }
        if (this.selectedTab === 'mon-secteur') {
          await this.$store.dispatch('reporter/loadResidences', agency.id)
          this.residences = this.localResidences.map((residence) => {
            return { id: residence._id, ...residence }
          })
        } else {
          this.$q.loading.show()
          this.$SowellProxy
            .loadAgencyResidences(this.selectedAgencyId)
            .then((response) => {
              this.residences = response.data.map((residence) => {
                return {
                  id: Math.trunc(residence.id),
                  name: residence.name
                }
              })
              this.$q.loading.hide()
            })
        }
      }
    },
    async selectResidence(residence) {
      this.selectedPlace = { label: '', value: '' }
      this.selectedSpot = { label: '', value: '' }
      this.selectedReason = { label: '', value: '' }
      if (residence) {
        this.selectedResidence = { label: residence.name, value: residence.id }
        if (this.selectedTab === 'mon-secteur') {
          await this.$store.dispatch('reporter/loadPlaces', residence.id)
          this.places = this.localPlaces.map((place) => {
            return { id: place._id, ...place }
          })
          this.places = this.places.filter(
            (place) =>
              (place.spots || []).filter(
                (spot) => Number(spot.category?.id) === this.issueTopCategoryId
              ).length
          )
        } else {
          this.$q.loading.show()
          this.isLoadingPlaces = true
          this.$SowellProxy
            .loadResidencePlaces(this.selectedResidenceId)
            .then((response) => {
              this.places = response.data
                .filter(
                  (place) =>
                    (place.spots || []).filter(
                      (spot) =>
                        Number(spot.category?.id) === this.issueTopCategoryId
                    ).length
                )
                .map((place) => {
                  return {
                    id: Math.trunc(place.id),
                    name: place.name
                  }
                })
              this.$q.loading.hide()
            })
            .finally(() => {
              this.isLoadingPlaces = false
            })
        }
      }
    },
    async selectPlace(place) {
      this.selectedSpot = { label: '', value: '' }
      this.selectedReason = { label: '', value: '' }

      if (place) {
        this.selectedPlace = { label: place.name, value: place.id }
        const topCategoryId = this.issueTopCategoryId
        if (!topCategoryId) {
          // NOTE: RUN-2927 — aucune catégorie exploitable : on n'affiche aucune
          // localisation plutôt que de laisser filer un TypeError
          this.spots = []
          this.spotSelectOptions = []
          return
        }
        if (this.selectedTab === 'mon-secteur' && !this.auditRelated) {
          const currentPlace = this.places.find((p) => p.id === place.id)
          this.spots = (currentPlace?.spots || []).filter(
            (spot) => Number(spot.category?.id) === topCategoryId
          )
          this.spotSelectOptions = this.sortList(
            this.$helpers.resourcesToFormOptions(this.spots)
          )
        } else if (this.auditRelated) {
          this.spots = (this.audit.place?.spots || []).filter(
            (spot) => Number(spot.category?.id) === topCategoryId
          )
          this.spotSelectOptions = this.sortList(
            this.$helpers.resourcesToFormOptions(this.spots)
          )
        } else {
          this.$q.loading.show()
          await this.$SowellProxy.loadPlaceSpots(place.id).then((response) => {
            this.spots = response
              .map((spot) => {
                return {
                  id: Math.trunc(spot.id),
                  name: spot.name,
                  categoryId: Math.trunc(spot.category?.id)
                }
              })
              .filter((spot) => Number(spot.categoryId) === topCategoryId)
            this.spotSelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions(this.spots)
            )
            this.$q.loading.hide()
          })
        }
      }
    },
    reset(excepted = []) {
      if (!excepted.includes('agencies')) {
        this.agencies = []
      }
      if (!excepted.includes('residences')) {
        this.residences = []
      }
      if (!excepted.includes('places')) {
        this.places = []
      }
      if (!excepted.includes('spots')) {
        this.spots = []
      }
      if (!excepted.includes('selectedAgencyId')) {
        this.selectedAgency = { label: '', value: '' }
      }
      if (!excepted.includes('selectedResidenceId')) {
        this.selectedResidence = { label: '', value: '' }
      }
      if (!excepted.includes('selectedPlaceId')) {
        this.selectedPlace = { label: '', value: '' }
      }
      if (!excepted.includes('selectedSpotId')) {
        this.selectedSpot = { label: '', value: '' }
      }
      if (!excepted.includes('selectedReasonId')) {
        this.selectedReason = { label: '', value: '' }
      }
      this.$nextTick(() => {
        this.v$.$reset()
      })
    },

    async populateFromCallbackLocation(location) {
      if (location.type === 'PLACE') {
        await this.$SowellProxy
          .loadAgencyResidenceOfPlace(location.id)
          .then(async ({ data }) => {
            const agency = data.residence.agency
            this.agencies = [agency]
            this.selectedAgency = { label: agency.name, value: agency.id }
            const residence = data.residence
            this.residences = [residence]
            this.selectedResidence = {
              label: residence.name,
              value: residence.id
            }
            this.places = [data]
            this.selectedPlace = { label: data.name, value: data.id }
          })
      }
    }
  },
  watch: {
    selectedTab() {
      this.reset()
      if (this.selectedTab === 'mon-secteur') {
        this.agencies = this.localAgencies.map((agency) => {
          return { id: agency._id, ...agency }
        })
      } else {
        if (this.callbackLocation) {
          this.populateFromCallbackLocation(this.callbackLocation)
          return
        }

        this.$q.loading.show()
        this.$SowellProxy
          .loadCompanyAgencies(this.company.id)
          .then((response) => {
            this.agencies = response.data.map((agency) => {
              return {
                id: agency.id,
                name: agency.name
              }
            })
            this.$q.loading.hide()
          })
      }
    },
    agencies(values) {
      if (this.auditRelated) {
        this.selectedAgency = { label: '', value: '' }
        this.selectedResidence = { label: '', value: '' }
        this.selectedPlace = { label: '', value: '' }
        this.selectedSpot = { label: '', value: '' }
        this.selectedReason = { label: '', value: '' }
      } else {
        // User is not a reporter or he has no agency so we move it to full lists
        if (values.length === 0 && this.selectedTab !== 'autre') {
          this.selectedTab = 'autre'
        }
      }
      // Automatically select the agency since there is only one available
      if (values && values.length === 1 && !this.callbackLocation) {
        this.selectAgency(values[0])
      }
    },
    residences() {
      if (this.auditRelated) {
        this.selectedAgency = { label: '', value: '' }
        this.selectedResidence = { label: '', value: '' }
        this.selectedPlace = { label: '', value: '' }
        this.selectedSpot = { label: '', value: '' }
        this.selectedReason = { label: '', value: '' }
      } else {
        // Automatically select the residence since there is only one available
        if (this.residences.length === 1 && !this.callbackLocation) {
          this.selectResidence(this.residences[0])
        }
      }
    },
    places() {
      if (this.auditRelated) {
        this.selectedAgency = { label: '', value: '' }
        this.selectedResidence = { label: '', value: '' }
        this.selectedPlace = { label: '', value: '' }
        this.selectedSpot = { label: '', value: '' }
        this.selectedReason = { label: '', value: '' }
        this.selectPlace(this.audit.place)
      } else {
        // Automatically select the place since there is only one available
        if (this.places.length === 1 && !this.callbackLocation) {
          this.selectPlace(this.places[0])
        }
      }
    },
    spots(values) {
      // Automatically select the spot since there is only one available
      if (values.length === 1) {
        this.selectedSpot = {
          label: values[0].name,
          value: values[0].id
        }
      }
    },
    selectedAgencyId(id) {
      if (!this.auditRelated && !this.callbackLocation) {
        const agency = this.agencies.find((a) => a.id === id)
        this.selectAgency(agency)
      }
    },
    selectedResidenceId(id) {
      if (id) {
        const residence = this.residences.find((r) => r.id === id)
        if (!this.auditRelated && !this.callbackLocation) {
          this.selectResidence(residence)
        }
      }
    },
    selectedPlaceId(id) {
      if (id) {
        let place
        if (this.auditRelated) {
          this.selectPlace(this.audit.place)
        } else {
          place = this.places.find((p) => p.id === id)
          this.selectPlace(place)
        }
      }
    },
    selectedSpotId(id) {
      if (id) {
        const spot = this.spots.find((s) => s.id === id)
        this.selectedSpot = { label: spot.name, value: spot.id }
      }
    },
    selectedReasonId(id) {
      if (id) {
        const reason = this.reasons.find((r) => r.value === id)
        this.selectedReason = { label: reason.label, value: reason.value }
      }
    }
  },
  async mounted() {
    await this.deviceReady()
    if (this.audit instanceof Object) {
      this.auditRelated = true
      this.issueCategory = this.getCheckpointCategory()
      await this.selectPlace(this.audit.place) // NOTE: this sets the spot for the Report
    } else {
      this.issueCategory = this.category
    }
    if (this.$route.query.location) {
      this.callbackLocation = JSON.parse(this.$route.query.location)
      this.selectedTab = 'autre'
      return
    }
    if (!this.auditRelated) {
      await this.$store.dispatch('reporter/loadAgencies')
      this.agencies = this.localAgencies.map((agency) => {
        return { id: agency._id, ...agency }
      })
    }
    // NOTE: RUN-2927 — une catégorie de premier niveau n'expose pas de
    // `reasons` (cf. store/company) et le fallback checklist peut en être une
    this.reasons = (this.issueCategory?.reasons || []).map((reason) => {
      return {
        value: Number(reason.id),
        label: reason.name
      }
    })
    this.reasonSelectOptions = this.reasons

    if (this.$route.query.spot) {
      this.$nextTick(() => {
        const spot = this.spots.find((s) => s.id === this.$route.query.spot)
        if (spot) {
          this.selectedSpot = { label: spot.name, value: spot.id }
        }
      })
    }

    if (this.$q.platform.is.capacitor) {
      eventBus.$on('keyboardDidShow', this.onKeyboardDidShow)
    }
  },
  beforeUnmount() {
    eventBus.$off('keyboardDidShow', this.onKeyboardDidShow)
  }
}
</script>

<style lang="stylus" scoped>
.buttonHover {
  left: 50%;
  z-index: 1;
}

.bordered {
  border-style: dotted dotted dotted dotted;
  border-color: silver;
  border-width: thin;
}

.removeImg {
  position: absolute;
  right: -15px;
  top: -10px;
  z-index: 1;
}

.qSelect {
  width: 100%;
}
</style>
<style lang="stylus">
.rgpdField {
  height: 10px;
  margin-top: 10px
}

.rgpd {
  font-style: italic;
}

.icon-rgpd {
  font-size: 23px;
  margin-right: 10px;
}

.rgpdButton {
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
}
</style>
